import { type SupportedLocale } from '@zep-us/i18n';

import { type BoardTypeValue } from '@/shared/features/board/utils/boardTypeLabel';

import { resolveContentLocale } from './boardLandingLocale.const';
import { type BoardLandingContentLocale } from './boardLandingLocale.const';

// ─── 샘플 보드 ────────────────────────────────────────────────────────────────

/**
 * 샘플 보드는 prod(quiz.zep.us)의 실 데이터다.
 *
 * 내부 라우팅(`/board/<id>`)이 아니라 prod 절대 URL 로 열고 새 창으로 띄운다. 내부
 * 라우팅으로 보내면 dev/preview 배포에서는 그 환경 DB 에 없는 보드라 404 가 되고,
 * 시안 검수 때마다 샘플이 죽어 보인다.
 */
const SAMPLE_BOARD_ORIGIN = 'https://quiz.zep.us';

const SAMPLE_BOARD_IDS: Record<
  BoardLandingContentLocale,
  Record<BoardTypeValue, string>
> = {
  ko: {
    WALL: '6aa7e68335ccbc100306fb5f',
    GROUP: '6aa7e6b40687f8e643ab08d1',
    TIMELINE: '6aa7e6630687f8e643aa6af3',
  },
  ja: {
    WALL: '6aa7e6c335ccbc10030782ad',
    GROUP: '6aa7e6c90687f8e643ab3534',
    TIMELINE: '6aa7e6bb0687f8e643ab176a',
  },
  id: {
    WALL: '6aa7e6da0687f8e643ab6245',
    GROUP: '6aa7e6e035ccbc100307c57b',
    TIMELINE: '6aa7e6d135ccbc1003079e82',
  },
  th: {
    WALL: '6aa7e6f00687f8e643ab9bac',
    GROUP: '6aa7e6f535ccbc100307f46b',
    TIMELINE: '6aa7e6e635ccbc100307d553',
  },
  en: {
    WALL: '6aa7e7060687f8e643abd36a',
    GROUP: '6aa7e70b0687f8e643abdfb8',
    TIMELINE: '6aa7e6fc0687f8e643abbba6',
  },
};

export function getSampleBoardUrl(
  locale: SupportedLocale,
  boardType: BoardTypeValue,
): string {
  return `${SAMPLE_BOARD_ORIGIN}/board/${
    SAMPLE_BOARD_IDS[resolveContentLocale(locale)][boardType]
  }`;
}

// ─── 가이드 ──────────────────────────────────────────────────────────────────

/**
 * 젭보드 가이드(채널톡 도움말) URL.
 *
 * 경로에서 로케일 세그먼트만 갈린다. ko·en·ja 만 문서가 준비돼 있어 나머지
 * 로케일은 이미지·샘플보드와 같은 규칙으로 en 으로 떨어진다.
 */
const GUIDE_LOCALE_SEGMENT: Record<
  BoardLandingContentLocale,
  'ko' | 'en' | 'ja'
> = {
  ko: 'ko',
  ja: 'ja',
  en: 'en',
  id: 'en',
  th: 'en',
};

export function getBoardGuideUrl(locale: SupportedLocale): string {
  const segment = GUIDE_LOCALE_SEGMENT[resolveContentLocale(locale)];

  return `https://docs.channel.io/zepquiz/${segment}/categories/%EB%B3%B4%EB%93%9C-%EC%A0%9C%EC%9E%91%ED%95%98%EA%B8%B0-72df5dff`;
}

/** CTA 목적지 — 보드 생성 페이지. */
export const BOARD_CREATE_PATH = '/board/new';
